import { Empty } from "antd";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import CardDropdown from "components/common/CardDropdown";
import { useAdvanceTable } from "hooks";
import useDonationsList from "hooks/donations/useDonationsList";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState } from "react";
import { Col, Dropdown, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";

const STATUS_STYLES = {
  ACTIVE: {
    bg: "#E8F7E3",
    text: "#2E7D32",
    border: "#D5EBD0",
    label: "ACTIVE",
  },
  PENDING: {
    bg: "#FFF4E5",
    text: "#ED6C02",
    border: "#FFE0B2",
    label: "PENDING",
  },
  NEW: {
    bg: "#FFF4E5",
    text: "#ED6C02",
    border: "#FFE0B2",
    label: "PENDING",
  },
  EXPIRED: {
    bg: "#FFF4E5",
    text: "#ED6C02",
    border: "#FFE0B2",
    label: "Expired",
  },
  DELETED: {
    bg: "#FDECEA",
    text: "#C62828",
    border: "#F5C6C3",
    label: "Deleted",
  },
};

export const DonationsTable = () => {
  const navigate = useNavigate();

  const { donations, loading, error } = useDonationsList();

  const formatID = (id) => `${id.slice(0, 8)}...`;
  const formatText = (text) => `${text.slice(0, 20)}...`;

  const columns = [
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ getValue }) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: 150 }}
          title={getValue()}
        >
          {getValue()}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "donor",
      header: "Donor",
      accessorFn: (row) => {
        const donor = row.donor;
        if (!donor) return "NULL";

        // Combine first + last name, or fallback to nickname
        const fullName = `${donor.firstName || ""} ${
          donor.lastName || ""
        }`.trim();
        return fullName || donor.nickName || "NULL";
      },
      cell: ({ row }) => {
        const donor = row.original.donor;
        if (!donor) return "NULL";
        const fullName = `${donor.firstName || ""} ${
          donor.lastName || ""
        }`.trim();
        return fullName || donor.nickName || "NULL";
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "doneeUser",
      header: "Donee",
      cell: ({ getValue }) => {
        const donee = getValue();
        if (!donee) return "NULL";
        const fullName = `${donee.firstName || ""} ${
          donee.lastName || ""
        }`.trim();
        return fullName || donee.nickName || "NULL";
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "amount",
      header: "Amount (USD)",
      cell: ({ getValue }) =>
        getValue() ? `$${getValue().toFixed(2)}` : "$0.00",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "balance",
      header: "Balance (USD)",
      cell: ({ getValue }) =>
        getValue() ? `$${getValue().toFixed(2)}` : "$0.00",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();
        const status = STATUS_STYLES[value] || {
          bg: "#F3F4F6",
          text: "#374151",
          border: "#E5E7EB",
          label: value || "Unknown",
        };
        return (
          <span
            className="py-2"
            style={{
              backgroundColor: status.bg,
              color: status.text,
              padding: "3px 8px",
              borderRadius: "6px",
              fontSize: "0.65rem",
              border: `1px solid ${status.border}`,
              cursor: "default",
              fontWeight: 600,
            }}
          >
            {status.label}
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "purposes",
      header: "Purposes",
      cell: ({ getValue }) => {
        const purposes = getValue();
        if (!purposes || purposes.length === 0)
          return <span className="text-xs">Not Provided</span>;
        const names = purposes.map((p) => p.name).join(", ");
        return (
          <span
            className="text-truncate d-inline-block text-xxs"
            style={{ maxWidth: 150 }}
            title={names}
          >
            {names}
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item
                onClick={() => navigate(`/olitask/donations/${item.id}`)}
              >
                View details
              </Dropdown.Item>
              {item.balance !== 0 && (
                <Guard privilege={PRIV.ISSUE_CREDIT}>
                  <Dropdown.Item
                    onClick={() =>
                      navigate(`/olitask/donations/${item.id}/issue-credit`)
                    }
                  >
                    Issue Credit
                  </Dropdown.Item>
                </Guard>
              )}
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: donations,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="hands-helping"
        title="Donations"
        subtitle="Manage and monitor all donations"
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      {donations && donations.length > 0 ? (
        <AdvanceTableProvider {...table}>
          <Row className="mb-2">
            <Col md={4}>
              <AdvanceTableSearchBox />
            </Col>
          </Row>

          <AdvanceTable
            headerClassName="bg-200 text-nowrap align-middle"
            rowClassName="btn-reveal-trigger text-nowrap align-middle"
            tableProps={{
              bordered: true,
              striped: true,
              className: "text-xs mb-0 overflow-hidden",
            }}
          />

          <div className="mt-3">
            <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
          </div>
        </AdvanceTableProvider>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 200 }}
        >
          <Empty description="No donations data available" />
        </div>
      )}
    </>
  );
};
